import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { CompanyLegalEntitiesApiService } from "src/services/company-legal-entities";
import { Props, State, LegalEntityOptionValue, LegalEntityQuickFilter } from "./interfaces";
import { useAppSelector } from "../../../custom-hooks/redux/redux";
import useAuthorization, { isAuthorized } from "../../../custom-hooks/useAuthorization/useAuthorization";
import { CompanyLegalEntityFilter } from "../../../models/company-accounts/company-legal-entities";
import { CompanyLegalEntityDTO } from "../../../models/company-accounts/company-legal-entities/company-legal-entity-dto";
import { QuickFilterOption } from "../../../reusable-components/QuickFilter/interfaces";
import QuickFilter from "../../../reusable-components/QuickFilter/QuickFilter";
import { AUTHORIZATIONS } from "../../../utils/constants/authorization-constants";

export const LegalEntityQuickfilter = <Q extends LegalEntityQuickFilter>({
  quickFilters,
  handleChangeQuickFilters,
  withOrWithoutLegalEntityOptions,
  allLegalEntitiesOption,
  isMultiSelect,
  allLegalEntities,
  authorizations,
  optionIsAuthorizedChecker,
  optionIncluded,
}: Props<Q>) => {
  const { currentUserAuthorizations } = useAppSelector((state) => state.currentUserReducer);

  const { t } = useTranslation();
  const tRP = "components.quickfilters.legal_entities"; // tRP - translation root path

  const [legalEntities, setCurrLegalEntities] = useState<CompanyLegalEntityDTO[]>([]);

  useEffect(() => {
    const includedLegalEntities = optionIncluded
      ? allLegalEntities.filter((LE) => optionIncluded(LE))
      : allLegalEntities;

    setCurrLegalEntities(includedLegalEntities);

    const newOptions: QuickFilterOption<LegalEntityOptionValue>[] = includedLegalEntities.map(({ name, code, id }) => ({
      name,
      value: { legalEntityIds: [id], withoutLegalEntity: false },
      code,
    }));

    setOptions((state) => ({ ...state, computedOptions: newOptions }));
  }, [allLegalEntities, optionIncluded]);

  const [loadLegalEntities, setLoadLegalEntities] = useState<boolean>(false);

  const canProcessDocumentNotLinkedToLe = useAuthorization([
    AUTHORIZATIONS.CompanyAccountsAccess.CanProcessDocumentNotLinkedToLe,
  ]);

  const [options, setOptions] = useState<State>({
    customOptions: [
      ...(allLegalEntitiesOption
        ? [
            {
              name: t(`${tRP}.all_legal_entities`),
              value: {
                legalEntityIds: optionIncluded
                  ? [...allLegalEntities.filter((LE) => optionIncluded(LE)).map((LE) => LE.id)]
                  : [],
                withoutLegalEntity: false,
              },
            },
          ]
        : []),
      ...(withOrWithoutLegalEntityOptions
        ? [
            {
              name: t(`${tRP}.with_without_legal_entity`),
              value: {
                withoutLegalEntity: false,
                legalEntityIds: optionIncluded
                  ? [...allLegalEntities.filter((LE) => optionIncluded(LE))].map((LE) => LE.id)
                  : [],
              },
            },
            ...(canProcessDocumentNotLinkedToLe
              ? [
                  {
                    name: t(`${tRP}.without_legal_entity`),
                    value: {
                      withoutLegalEntity: true,
                      legalEntityIds: optionIncluded
                        ? [...allLegalEntities.filter((LE) => optionIncluded(LE))].map((LE) => LE.id)
                        : [],
                    },
                  },
                ]
              : []),
          ]
        : []),
    ],
    computedOptions: [],
  });

  useEffect(() => {
    if (legalEntities) {
      const newOptions: QuickFilterOption<LegalEntityOptionValue>[] = legalEntities.map(({ name, code, id }) => ({
        name,
        value: { legalEntityIds: [id], withoutLegalEntity: false },
        code,
      }));

      setOptions((state) => ({ ...state, computedOptions: newOptions }));
    }
  }, [legalEntities]);

  const loadNewItems = () => {
    setLoadLegalEntities(true);
  };

  const loadItemCallback = useCallback(
    async (signal: AbortSignal) => {
      setLoadLegalEntities(true);
      try {
        const response = await new CompanyLegalEntitiesApiService().loadItems(
          {
            ids: [],
            contextAuthorizations: authorizations ? [...authorizations] : [],
          } as CompanyLegalEntityFilter,
          undefined,
          undefined, // so that service API default sort is applied
          { pageNumber: 0, pageSize: 10000 },
          undefined,
          60,
          signal
        );
        if (!response.error) {
          const optionsIncluded = optionIncluded ? response.items.filter((le) => optionIncluded(le)) : response.items;

          const authorizedLEs = authorizations
            ? optionIsAuthorizedChecker
              ? optionsIncluded.filter((le) =>
                  optionIsAuthorizedChecker([...authorizations], currentUserAuthorizations, [le.id])
                )
              : isAuthorized([...authorizations], currentUserAuthorizations)
              ? optionsIncluded
              : []
            : optionsIncluded;

          setCurrLegalEntities(authorizedLEs);
        }
      } catch (error: any) {
        console.log(error);
      } finally {
        setLoadLegalEntities(false);
      }
    },
    [authorizations, optionIsAuthorizedChecker, optionIncluded]
  );

  useEffect(() => {
    const abortController = new AbortController();

    loadLegalEntities && loadItemCallback(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [loadLegalEntities]);

  return (
    <QuickFilter
      loading={loadLegalEntities}
      loadNewItems={loadNewItems}
      allItems={legalEntities}
      options={[...options.customOptions, ...options.computedOptions]}
      quickFilters={quickFilters}
      quickFilter="legalEntities"
      handleChangeQuickFilter={handleChangeQuickFilters}
      shrinkedLabel={t(`${tRP}.shrinked_label`)}
      isMultiSelect={isMultiSelect}
      multiSelectField="legalEntityIds"
      customWidth={"170px"}
    />
  );
};
